import { useEffect, useRef, useState } from 'react'
import imageNotFound from '../../assets/icons/image-not-found.svg'
import userImageNotFound from '../../assets/icons/user-image-not-found.svg'
import chefHat from '../../assets/icons/chef-hat.svg'
import {
	acquireSlot,
	isWarmed,
	markWarmed,
	prefetchImage,
	sleep,
} from '../../services/imageLoadQueue'
import {
	resolveUserAvatarSrc,
	initialsAvatarDataUri,
} from '../../services/functions'

const isRemoteUrl = (v) => typeof v === 'string' && /^https?:\/\//i.test(v)
const MAX_RETRIES = 3
// How long a slot may be held by an <img> that has neither loaded nor errored.
//
// ⚠️ Nothing else ever takes it back. A request that STALLS — no load event, no
// error event, which is exactly what an un-CDN'd Contabo bucket does under load
// — used to hold its slot for the life of the page, permanently shrinking the
// pool. Six of those and every later image in the app waits forever for a slot
// that is never coming, so `src` is never assigned and no <img> is even
// rendered. Releasing here does NOT cancel the request; it only stops that
// request from blocking everyone else.
const SLOT_STALL_MS = 10000
// Warm the cache several screens out (low-priority lane), then mount the
// on-screen <img> ~1.5 screens out. By the time the box is visible the bytes
// are already cached, so it paints with a clean fade instead of flashing in
// mid-scroll. (Home-widget images are additionally warmed up-front, on data
// load, regardless of scroll position — see HomeWidgets.)
const PREFETCH_MARGIN = '3600px'
const RENDER_MARGIN = '1200px'

// The placeholder div is often 0px tall; a zero-height target never reports as
// intersecting. Walk up to the first ancestor with real layout so the observer
// has something measurable to watch.
const resolveObserveTarget = (node) => {
	let target = node
	let hops = 0
	while (
		target?.parentElement &&
		target.getBoundingClientRect().height === 0 &&
		hops < 5
	) {
		target = target.parentElement
		hops += 1
	}
	return target
}

export const ImageLoader = ({
	imageType = 'default',
	image: rawImage,
	css = '',
	zoom = false,
	styles = {},
	// Name to derive initials from when a user avatar is missing (e.g. "Alessandra
	// Pizzarini" -> "AP"). Accepts a string or {name, surname}/{fullname}.
	fallbackName = null,
	// Draw nothing at all when the picture cannot be loaded, instead of the
	// generic "no image" icon. For a thumbnail the icon explains the gap; on a
	// surface that IS the picture — a full-screen Storia — it reads as breakage,
	// and the frame behind it already looks deliberate on its own.
	hideOnError = false,
	// Load this one NOW: no lazy gate, no waiting for a concurrency slot.
	//
	// The throttle exists to stop a scrolling LIST opening dozens of parallel
	// connections to an un-CDN'd bucket. A single foreground picture is not a
	// burst, and making the one image the member is actually looking at queue
	// behind background work is backwards — that is how a full-screen Storia
	// ended up showing only its blurred backdrop while the feed behind it
	// loaded. Use for surfaces that ARE the picture; never for list items.
	priority = false,
}) => {
	// Predefined avatars are stored as a name ("avatar11") that the server turns
	// into a broken /user-avatars/avatar11 URL. For user avatars, run the source
	// through resolveUserAvatarSrc so those map back to the bundled local asset;
	// real uploaded URLs (and every non-user image) pass through unchanged.
	const image =
		imageType === 'user' ? resolveUserAvatarSrc(rawImage) || rawImage : rawImage

	let notFoundImage = imageNotFound
	switch (imageType) {
		case 'user': {
			// No photo → the person's initials when we know their name, so a real
			// user never falls back to a generic/brand icon. Callers pass either a
			// string or the user-ish object they already have.
			const n = fallbackName
			const nameStr =
				typeof n === 'string'
					? n
					: n?.fullname ||
					  n?.subscriber_full_name ||
					  [n?.name, n?.surname].filter(Boolean).join(' ')
			notFoundImage = initialsAvatarDataUri(nameStr) || userImageNotFound
			break
		}
		case 'recipe':
			notFoundImage = chefHat
			break
	}

	const remote = isRemoteUrl(image)
	const noImageCss = !image ? css + ' no-image' : css

	// Who may skip the queue. A warm url is already in the browser cache, so a
	// slot buys nothing and costs a wait: it was the reason a Storia whose cover
	// the tray had ALREADY drawn still came up blank — cached bytes, sitting
	// behind six cold ones.
	//
	// `isWarmed` is optimistic — prefetchImage marks a url before its bytes have
	// landed — so at worst this is one request running alongside an in-flight
	// prefetch of the same url, which shares the same cache entry. That is a far
	// better trade than an unbounded wait for a slot.
	const skipsQueue = (url) => priority || isWarmed(url)

	const placeholderRef = useRef(null)
	const imgRef = useRef(null)
	const releaseRef = useRef(null) // active concurrency-slot release fn
	const stallTimerRef = useRef(null) // watchdog on a slot that never settles
	const attemptRef = useRef(0) // retry counter
	const cancelledRef = useRef(false)
	// Captured once at mount: was this URL already in the cache (prefetched)? A
	// cache hit paints in the same frame, so we skip the fade for it entirely —
	// fading an already-visible image is itself the flicker the user sees.
	const wasWarmedRef = useRef(remote && isWarmed(image))

	// Retries exhausted for the CURRENT url. Everything the failure changes —
	// the sizing, the zoom container, which src is shown — is derived from this
	// rather than written into state, so nothing can outlive the url it belongs
	// to. It used to be three separate setState calls, and the sizing one
	// (`maxWidth: 300px`) then applied to every LATER picture this same
	// component instance was given.
	const [failed, setFailed] = useState(false)
	const [loading, setLoading] = useState(true)
	// Bumped on each retry to remount the <img> and re-request the SAME url
	// (no cache-busting query) — see handleError.
	const [reloadTick, setReloadTick] = useState(0)
	// Remote URLs stay null until a slot is free (throttled). Local assets /
	// data: / blob: URLs render immediately as before.
	const [src, setSrc] = useState(
		remote && !skipsQueue(image) ? null : image || notFoundImage,
	)

	// Remote images mount once their box nears the viewport, so a page-enter
	// never queues every off-screen image at once. Local images, browsers
	// without IntersectionObserver, and already-warm (cached) images are treated
	// as immediately in-view so they paint instantly with no flash.
	const supportsIO = typeof IntersectionObserver !== 'undefined'
	const [inView, setInView] = useState(
		!remote || !supportsIO || priority || isWarmed(image),
	)

	// ⚠️ A parent can hand this same component instance a DIFFERENT picture — it
	// is the `image` prop that changes, not the element. The Storie viewer does
	// exactly that: Swiper keeps one slide per author and the story inside it is
	// swapped in place, so a single ImageLoader serves every story in that slot.
	// Anything left over from the previous url is therefore wrong for the new
	// one, and a failure was the worst of it: one missing file made every later
	// story in that slot render at the fallback's 300px inside a 390px frame.
	//
	// Reset during render rather than in an effect, which is React's own answer
	// to "a prop changed" — an effect would let the new picture paint one frame
	// still wearing the old one's state.
	const [lastImage, setLastImage] = useState(image)
	if (image !== lastImage) {
		setLastImage(image)
		setFailed(false)
		setLoading(true)
		setReloadTick(0)
		// Blank a remote src so the previous story's photo does not sit under the
		// new story's text while the new bytes are on their way. A local asset has
		// nothing to wait for and is shown straight away, as on mount.
		setSrc(remote && !skipsQueue(image) ? null : image || notFoundImage)
		attemptRef.current = 0
		cancelledRef.current = false
		// Re-answer "was this one already cached?" for the new url, or a warm
		// picture would fade in and a cold one would not.
		wasWarmedRef.current = remote && isWarmed(image)
	}

	// Far tier: warm the cache well before render so the on-screen <img> is a
	// cache hit. Runs on the low-priority prefetch lane (yields to renders).
	useEffect(() => {
		if (!remote || !supportsIO || isWarmed(image)) return
		const node = placeholderRef.current
		if (!node) return
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					prefetchImage(image)
					observer.disconnect()
				}
			},
			{ rootMargin: PREFETCH_MARGIN },
		)
		observer.observe(resolveObserveTarget(node))
		return () => observer.disconnect()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [image, remote])

	// Near tier: flip `inView` once the box scrolls within ~1.5 screens.
	useEffect(() => {
		if (inView) return
		const node = placeholderRef.current
		if (!node || !supportsIO) {
			setInView(true)
			return
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					setInView(true)
					observer.disconnect()
				}
			},
			{ rootMargin: RENDER_MARGIN },
		)
		observer.observe(resolveObserveTarget(node))
		return () => observer.disconnect()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView, image])

	const releaseSlot = () => {
		if (stallTimerRef.current) {
			clearTimeout(stallTimerRef.current)
			stallTimerRef.current = null
		}
		if (releaseRef.current) {
			releaseRef.current()
			releaseRef.current = null
		}
	}

	// Take the slot, and arm the watchdog that gives it back if this <img> never
	// says anything either way. See SLOT_STALL_MS.
	const holdSlot = (release) => {
		releaseRef.current = release
		stallTimerRef.current = setTimeout(releaseSlot, SLOT_STALL_MS)
	}

	// Acquire a slot, then assign the real URL. The single <img> below performs
	// the one request; its onLoad / onError drive retry + slot release. No
	// separate new Image() preload here — that doubled every request to S3.
	useEffect(() => {
		if (!remote) {
			setSrc(image || notFoundImage)
			setLoading(false)
			return
		}
		if (!inView) return

		cancelledRef.current = false
		attemptRef.current = 0

		// Straight to the picture, taking no slot at all. `await acquireSlot()`
		// has no timeout and no upper bound: while the six slots are busy this
		// component renders NO <img> element whatsoever, because `src` is still
		// null. On a full-screen Storia that left the blurred backdrop — plain
		// CSS, never queued — as the only thing on screen, while the progress
		// bar ran on to the next Story regardless.
		if (skipsQueue(image)) {
			setSrc(image)
			return undefined
		}

		let active = true

		;(async () => {
			const release = await acquireSlot()
			if (!active) {
				release()
				return
			}
			holdSlot(release)
			setSrc(image)
		})()

		return () => {
			active = false
			cancelledRef.current = true
			releaseSlot()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [image, inView])

	const showFallback = () => {
		setFailed(true)
		setSrc(notFoundImage)
		setLoading(false)
	}

	// What the failure looks like, worked out fresh on every render so it belongs
	// to the url currently being shown and to no other. A group avatar, and any
	// caller that asked to hide, simply draw nothing; everything else shows the
	// "no image" icon at a size that suits an icon rather than a photo.
	const zoomable = zoom && !failed
	const style = !failed
		? styles
		: imageType === 'group' || hideOnError
		? { display: 'none' }
		: { ...styles, width: '100%', maxWidth: '300px' }

	const handleLoad = () => {
		setLoading(false)
		releaseSlot()
		// Remember this URL is now cached so future mounts skip the lazy gate.
		if (remote) markWarmed(image)
		// Tell any ancestor slider (e.g. a Swiper with autoHeight) to recompute
		// its layout now that this image has real dimensions — otherwise it stays
		// sized to the empty, pre-load placeholder and clips the image.
		imgRef.current?.dispatchEvent(
			new CustomEvent('imageloader:load', { bubbles: true }),
		)
	}

	const handleError = async () => {
		if (cancelledRef.current) return
		// Settle the (local) fallback without looping; only remote URLs retry.
		if (!remote) {
			showFallback()
			return
		}
		// Free the slot while we back off so other images aren't blocked.
		releaseSlot()
		if (attemptRef.current >= MAX_RETRIES) {
			showFallback()
			return
		}
		const n = (attemptRef.current += 1)
		await sleep(Math.min(8000, 700 * 2 ** (n - 1)) + Math.random() * 250)
		if (cancelledRef.current) return
		// A priority image does not start queueing just because it failed once —
		// it is still the thing on screen. The backoff above already paces it.
		if (!skipsQueue(image)) {
			const release = await acquireSlot()
			if (cancelledRef.current) {
				release()
				return
			}
			holdSlot(release)
		}
		// Retry the SAME url (no cache-busting query) and remount the <img> via a
		// changing key so the browser re-issues the request. A cache-busted url
		// (?r=n) turned one failure into several DISTINCT, uncacheable requests —
		// amplifying the burst that causes ERR_CONNECTION_RESET and defeating any
		// CDN/browser cache. `n` only paces the backoff above.
		void n
		setSrc(image)
		setReloadTick((t) => t + 1)
	}

	// Clean fade-in: hold at opacity 0 until the bytes have loaded, then ease in.
	// Replaces the old `.d-true.fade` rule (which animated opacity 1→0 on mount,
	// itself a source of the mid-scroll flicker). Already-warm (cached) images
	// paint synchronously, so they go straight to opacity 1 with no transition —
	// fading a cache hit is what reads as a flash while scrolling.
	const warm = wasWarmedRef.current
	const fadeStyle = {
		...style,
		opacity: warm || !loading ? 1 : 0,
		transition: warm ? 'none' : 'opacity 0.35s ease-in-out',
	}

	// NOTE: deliberately no `loading="lazy"`. Our two IntersectionObserver tiers
	// already mount + warm the image ~1.5 screens ahead of the viewport; native
	// lazy-loading adds a second gate that defers the decode until the box is
	// almost on-screen, so the fade would play right as it scrolls into view —
	// the exact flicker we're removing. Without it the cached bytes paint early
	// and the fade completes off-screen.
	const img = src && (
		<img
			key={reloadTick}
			ref={imgRef}
			className={noImageCss + ' d-true'}
			src={src}
			style={fadeStyle}
			decoding="async"
			onLoad={handleLoad}
			onError={handleError}
		/>
	)

	return (
		<>
			<div
				ref={placeholderRef}
				className={noImageCss + ' image-loader d-' + loading}
				style={{ background: '#e8e8e8', borderRadius: '4px' }}
			/>
			{img &&
				(zoomable ? (
					<span className="swiper-zoom-container">{img}</span>
				) : (
					img
				))}
		</>
	)
}
